/**
 * UserAvatar — robust profile picture component that gracefully degrades
 * to a colourful initials badge when:
 *   • the user has no profile_picture set, or
 *   • the image fails to load (404, expired R2 link, etc.)
 *
 * Profile picture URLs can be either:
 *   • absolute URL (already includes http/https) → use as-is
 *   • bare filename                              → prefixed with /api/profile/picture/
 *   • relative path starting with /api/...       → prefixed with BACKEND_URL
 *
 * NEW (iter_397): when no real picture is set AND no `chosenAvatarId` was
 * supplied, we deterministically pick one of the AI-generated gallery
 * avatars based on the userId. The same userId always renders the same
 * avatar across sessions. Initials remain the last-resort fallback if
 * the gallery is unreachable or all images fail to load.
 */
import { useState, useEffect } from 'react';
import {
  preloadAvatarGallery,
  resolveGalleryAvatarUrl,
} from '../../utils/avatarGallery';
import { cfResize } from '../../utils/cfImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Deterministic colour from username so each user always gets the same hue.
function colourFor(seed = '') {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const hue = Math.abs(h) % 360;
  return `hsl(${hue} 60% 32%)`;
}

function initialsFor(username = '?') {
  const cleaned = username.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return (cleaned[0] || '?') + (cleaned[1] || '');
}

function resolveSrc(profilePicture, profileCdnUrl) {
  // CDN URL wins — it's the global edge.
  if (profileCdnUrl && typeof profileCdnUrl === 'string') return profileCdnUrl;
  if (!profilePicture) return null;
  if (typeof profilePicture !== 'string') return null;
  if (profilePicture.startsWith('http')) return profilePicture;
  if (profilePicture.startsWith('/')) return `${BACKEND_URL}${profilePicture}`;
  // bare filename
  return `${BACKEND_URL}/api/profile/picture/${profilePicture}`;
}

// Warm the gallery cache on first mount so deterministic fallbacks resolve
// without a flicker.
let _galleryWarmed = false;

export function UserAvatar({
  username,
  profilePicture,
  profileCdnUrl,
  chosenAvatarId,
  userId,
  gender,
  size = 80,
  className = '',
  rounded = 'rounded-full',
  testId,
}) {
  const [errored, setErrored] = useState(false);
  const [galleryTick, setGalleryTick] = useState(0); // bumps after gallery loads

  useEffect(() => {
    if (_galleryWarmed) return;
    _galleryWarmed = true;
    // Warm the mixed pool first (fastest paint), then the gender pool.
    preloadAvatarGallery().then(() => setGalleryTick((t) => t + 1));
    if (gender === 'male' || gender === 'female') {
      preloadAvatarGallery(gender).then(() => setGalleryTick((t) => t + 1));
    }
  }, [gender]);

  // Reset error state when profilePicture prop changes
  useEffect(() => { setErrored(false); }, [profilePicture, profileCdnUrl, chosenAvatarId]);

  // Resolve uploaded src then size it down via CF Image Resizing when on
  // the CDN. Tiny avatars (sidebar / row) request 64-px; cards request 256.
  const rawUploadedSrc = resolveSrc(profilePicture, profileCdnUrl);
  const uploadedSrc = rawUploadedSrc
    ? cfResize(rawUploadedSrc, {
        w: size <= 64 ? 64 : size <= 128 ? 128 : size <= 256 ? 256 : 512,
        h: size <= 64 ? 64 : size <= 128 ? 128 : size <= 256 ? 256 : 512,
      })
    : null;
  const fallbackSrc =
    galleryTick !== undefined &&
    (!uploadedSrc || errored)
      ? resolveGalleryAvatarUrl({ chosenAvatarId, userId, gender, size })
      : null;
  const src = errored ? fallbackSrc : (uploadedSrc || fallbackSrc);
  const showImage = !!src && !(errored && !fallbackSrc);
  const initials = initialsFor(username);

  return (
    <div
      className={`${rounded} overflow-hidden flex items-center justify-center text-white font-['Chakra_Petch'] uppercase select-none ${className}`}
      style={{
        width: size,
        height: size,
        background: showImage ? '#0a0a0a' : `linear-gradient(135deg, ${colourFor(username || '?')}, #1a1a1a)`,
        fontSize: Math.max(10, Math.floor(size * 0.36)),
        letterSpacing: '0.03em',
      }}
      data-testid={testId}
      aria-label={username || 'user avatar'}
    >
      {showImage ? (
        <img
          src={src}
          alt={username || ''}
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
          onError={() => setErrored(true)}
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
}

export default UserAvatar;
